import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Leaf,
  TrendingUp,
  TrendingDown,
  Wallet,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Loader2,
} from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#1a2e22", "#00c853", "#81c784", "#e0e7e3", "#2d4a3e"];

const getCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const dashboardRef = useRef(null);
  const user = getCurrentUser();
  const company = user?.company_name || "—";

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch dashboard stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateForFile = () => new Date().toISOString().split("T")[0];

  const exportDashboardPDF = async () => {
    if (!stats || !dashboardRef.current) return;
    setExporting(true);
    // Brief delay so the disabled button + toast render before capture
    await new Promise((r) => setTimeout(r, 100));
    try {
      const node = dashboardRef.current;

      // Sections to capture, in order. Each gets its own A4-landscape page.
      // expand=true forces the element to a wider width before capture so
      // narrow grid columns (like the pie/donut card) render legibly in landscape.
      const sections = [
        { selector: '[data-testid="kpi-grid"]',                 title: "Key Metrics",             expand: false },
        { selector: '[data-testid="chart-emissions-trend"]',    title: "Carbon Emissions Trend",  expand: true  },
        { selector: '[data-testid="chart-scope-distribution"]', title: "Emissions by Scope",      expand: true  },
        { selector: '[data-testid="chart-category-emissions"]', title: "Emissions by Category",   expand: true  },
      ];

      // Temporarily expand an element to a wider width and wait for Recharts ResizeObserver
      // to redraw the SVG before capturing. Returns a restore() function.
      const expandToWidth = async (el, targetPx = 1400) => {
        const original = {
          width: el.style.width,
          maxWidth: el.style.maxWidth,
          flex: el.style.flex,
          gridColumn: el.style.gridColumn,
        };
        el.style.width = `${targetPx}px`;
        el.style.maxWidth = "none";
        el.style.flex = "0 0 auto";
        el.style.gridColumn = "unset";
        // Force reflow + wait for ResponsiveContainer + chart redraw
        void el.offsetHeight;
        await new Promise((r) => setTimeout(r, 600));
        return () => {
          el.style.width = original.width;
          el.style.maxWidth = original.maxWidth;
          el.style.flex = original.flex;
          el.style.gridColumn = original.gridColumn;
        };
      };

      const pdf = new jsPDF("l", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const headerH = 22;
      const contentTop = headerH + margin;

      const drawHeader = (sectionTitle) => {
        pdf.setFillColor(26, 46, 34);
        pdf.rect(0, 0, pageWidth, headerH, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(16);
        pdf.text("Ledger Green — Dashboard", margin, 10);
        pdf.setFontSize(11);
        pdf.setTextColor(129, 230, 168);
        pdf.text(company, margin, 16.5);
        pdf.setFontSize(9);
        pdf.setTextColor(220, 230, 224);
        pdf.text(
          `Generated: ${new Date().toLocaleString()}`,
          pageWidth - margin,
          16.5,
          { align: "right" }
        );
        pdf.setTextColor(26, 46, 34);
        pdf.setFontSize(13);
        pdf.text(sectionTitle, margin, headerH + 8);
      };

      let renderedAny = false;
      for (let i = 0; i < sections.length; i++) {
        const { selector, title, expand } = sections[i];
        const el = node.querySelector(selector);
        if (!el) continue;

        let restore = null;
        if (expand) restore = await expandToWidth(el, 1400);

        const canvas = await html2canvas(el, {
          scale: 2,
          backgroundColor: "#ffffff",
          useCORS: true,
          logging: false,
          windowWidth: el.scrollWidth,
          windowHeight: el.scrollHeight,
        });

        if (restore) restore();

        const imgData = canvas.toDataURL("image/png");

        if (renderedAny) pdf.addPage();
        drawHeader(title);

        const availableW = pageWidth - margin * 2;
        const availableH = pageHeight - contentTop - 12 - margin;
        const yStart = contentTop + 12;

        const aspect = canvas.width / canvas.height;
        let imgW = availableW;
        let imgH = imgW / aspect;
        if (imgH > availableH) {
          imgH = availableH;
          imgW = imgH * aspect;
        }
        const x = (pageWidth - imgW) / 2;
        const y = yStart + (availableH - imgH) / 2;

        pdf.addImage(imgData, "PNG", x, y, imgW, imgH);
        renderedAny = true;
      }

      if (!renderedAny) {
        throw new Error("No dashboard sections were available to capture");
      }

      // Footer page numbers
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(120);
        pdf.text(
          `Ledger Green — ${company}`,
          margin,
          pageHeight - 4
        );
        pdf.text(
          `Page ${i} of ${pageCount}`,
          pageWidth - margin,
          pageHeight - 4,
          { align: "right" }
        );
      }

      pdf.save(
        `Dashboard_${company.replace(/\s+/g, "_")}_${formatDateForFile()}.pdf`
      );
      toast.success("Dashboard PDF downloaded");
    } catch (err) {
      console.error(err);
      toast.error("Failed to export PDF");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a2e22]" />
      </div>
    );
  }

  const scopeData = [
    { name: "Scope 1", value: stats?.scope1_emissions || 0, color: "#f59e0b" },
    { name: "Scope 2", value: stats?.scope2_emissions || 0, color: "#3b82f6" },
    { name: "Scope 3", value: stats?.scope3_emissions || 0, color: "#10b981" },
  ].filter(d => d.value > 0);

  return (
    <div ref={dashboardRef} className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 animate-fade-in-up">
        <div>
          <h1 className="text-4xl font-bold text-[#1a2e22] font-['Outfit'] tracking-tight">
            Dashboard
          </h1>
          <p className="text-[#5c6b63] mt-2" data-testid="dashboard-subtitle">
            <span className="font-semibold text-[#00c853]">{company}</span>
            <span className="mx-2 text-[#c8d4cc]">·</span>
            Your sustainability performance at a glance
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            className="btn-primary flex items-center gap-2"
            onClick={exportDashboardPDF}
            disabled={exporting || !stats}
            data-testid="dashboard-export-pdf"
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Capturing…
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Export PDF
              </>
            )}
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="kpi-grid">
        <Card className="kpi-card animate-fade-in-up" data-testid="kpi-carbon">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-[#5c6b63] font-medium">Total Carbon Footprint</p>
                <p className="text-3xl font-bold text-[#1a2e22] mt-2 font-['Outfit']">
                  {stats?.total_carbon_kg?.toLocaleString() || 0}
                </p>
                <p className="text-sm text-[#5c6b63] mt-1">kg CO₂e</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Leaf className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card animate-fade-in-up animation-delay-100" data-testid="kpi-transactions">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-[#5c6b63] font-medium">Total Transactions</p>
                <p className="text-3xl font-bold text-[#1a2e22] mt-2 font-['Outfit']">
                  {stats?.total_transactions?.toLocaleString() || 0}
                </p>
                <p className="text-sm text-[#5c6b63] mt-1">processed</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                <Activity className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card animate-fade-in-up animation-delay-200" data-testid="kpi-spend">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-[#5c6b63] font-medium">Total Spend</p>
                <p className="text-3xl font-bold text-[#1a2e22] mt-2 font-['Outfit']">
                  £{stats?.total_spend?.toLocaleString() || 0}
                </p>
                <div className="flex items-center gap-1 mt-1">
                  <ArrowUpRight className="w-4 h-4 text-red-500" />
                  <span className="text-sm text-red-500">Expenses</span>
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card animate-fade-in-up animation-delay-300" data-testid="kpi-income">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-[#5c6b63] font-medium">Total Income</p>
                <p className="text-3xl font-bold text-[#1a2e22] mt-2 font-['Outfit']">
                  £{stats?.total_income?.toLocaleString() || 0}
                </p>
                <div className="flex items-center gap-1 mt-1">
                  <ArrowDownRight className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm text-emerald-500">Revenue</span>
                </div>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Emissions Trend */}
        <Card className="lg:col-span-8 chart-container" data-testid="chart-emissions-trend">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1a2e22] font-['Outfit']">
              Carbon Emissions Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats?.monthly_emissions || []}>
                  <defs>
                    <linearGradient id="colorCarbon" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00c853" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#00c853" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e7e3" />
                  <XAxis 
                    dataKey="month" 
                    stroke="#5c6b63" 
                    fontSize={12}
                    tickLine={false}
                  />
                  <YAxis 
                    stroke="#5c6b63" 
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e0e7e3",
                      borderRadius: "12px",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.08)"
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="carbon_kg"
                    stroke="#00c853"
                    strokeWidth={2}
                    fill="url(#colorCarbon)"
                    name="Carbon (kg CO₂e)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Scope Distribution */}
        <Card className="lg:col-span-4 chart-container" data-testid="chart-scope-distribution">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1a2e22] font-['Outfit']">
              Emissions by Scope
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={scopeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {scopeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => [`${value.toFixed(2)} kg CO₂e`, ""]}
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e0e7e3",
                      borderRadius: "12px"
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Emissions by Category */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Card className="lg:col-span-7 chart-container" data-testid="chart-category-emissions">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1a2e22] font-['Outfit']">
              Emissions by Category
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.emissions_by_category?.slice(0, 8) || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e7e3" horizontal={false} />
                  <XAxis type="number" stroke="#5c6b63" fontSize={12} />
                  <YAxis 
                    dataKey="category" 
                    type="category" 
                    stroke="#5c6b63" 
                    fontSize={12}
                    width={120}
                    tickLine={false}
                  />
                  <Tooltip 
                    formatter={(value) => [`${value.toFixed(2)} kg CO₂e`, "Carbon"]}
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e0e7e3",
                      borderRadius: "12px"
                    }}
                  />
                  <Bar 
                    dataKey="carbon_kg" 
                    fill="#1a2e22" 
                    radius={[0, 4, 4, 0]}
                    name="Carbon (kg CO₂e)"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card className="lg:col-span-5" data-testid="recent-transactions">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1a2e22] font-['Outfit']">
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {stats?.recent_transactions?.slice(0, 5).map((t, idx) => (
                <div 
                  key={idx} 
                  className="flex items-center justify-between py-3 border-b border-[#e0e7e3] last:border-0"
                >
                  <div>
                    <p className="font-medium text-[#1a2e22]">{t.account}</p>
                    <p className="text-sm text-[#5c6b63]">{t.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm font-medium text-[#1a2e22]">
                      {t.carbon_kg?.toFixed(2)} kg
                    </p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      t.emission_scope === "Scope 1" ? "bg-amber-100 text-amber-700" :
                      t.emission_scope === "Scope 2" ? "bg-blue-100 text-blue-700" :
                      t.emission_scope === "Scope 3" ? "bg-emerald-100 text-emerald-700" :
                      "bg-gray-100 text-gray-700"
                    }`}>
                      {t.emission_scope}
                    </span>
                  </div>
                </div>
              ))}
              {(!stats?.recent_transactions || stats.recent_transactions.length === 0) && (
                <p className="text-center text-[#5c6b63] py-8">
                  No transactions yet. Upload CSV data to get started.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
